/**
 * CANLI VERİ — tazeliğin ortak sözleşmesi (22 Eyl 2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden var — "senkron zamanı" diye bir şey olmamalı
 * ═══════════════════════════════════════════════════════════════════════════
 * Önceki düzende tazeliği sabit saatler taşıyordu: reklam 15 dakikada bir,
 * mağaza saatte bir kopyalanıyor, ekran ve ajan o kopyayı okuyordu. Kullanıcı
 * "Son senkron 32 dakika önce" görüyor, ajan 60 dakikalık siparişle cevap
 * veriyordu. Zamanlayıcı bir AĞ olacakken tazeliğin TEK kaynağı olmuştu.
 *
 * Yeni düzende üç şey birlikte çalışıyor:
 *
 *   1. **İzlenen alan canlı tutulur.** Panel açıkken (SSE bağlantısı var) ya
 *      da API anahtarıyla okunurken alanın her bağlantısı `*WatchedSeconds`
 *      aralığında tazelenir. Kimse bakmıyorsa `*BackgroundSeconds` — bildirim
 *      ve otomasyonun gördüğü ritim bu.
 *   2. **Veri değişince ekrana itilir.** Worker yazdığı anda `LiveEvent`
 *      yayınlıyor, açık ekran kendi kopyasını tazeliyor. Sayfa yenilemek yok.
 *   3. **Ajan bayat veriyle cevap vermez.** Okuma aracı çalışmadan önce
 *      veri `*WatchedSeconds`tan eskiyse tazeleme bekleniyor.
 *
 * Kalan tek gecikme platformun KENDİ raporlama gecikmesi (Meta harcamayı
 * dakikalar içinde işler). Platformda ne yazıyorsa burada o görünür.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden "her saniye" değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Platform kotaları hesap başına: Meta BUC saatlik, Google Basic erişim
 * günlük işlem sayısı. Aralıklar kotanın rahat taşıyacağı ve platformun kendi
 * gecikmesinin altında kalan değerler. Meta kota başlığı %50'yi geçen hesap
 * kendiliğinden arka plan ritmine iniyor (worker `sync.service.ts`).
 */

/// Arayüzün abone olduğu konular. Olay VERİ TAŞIMAZ — yalnızca "bu alanda
/// veri değişti, ekranın okuduğu kopyayı tazele" der. Veri her zaman kendi
/// ucundan, kiracı yalıtımından geçerek okunur.
export const LIVE_TOPICS = [
  /// Reklam hesabı senkronu yazdı: kampanya, metrik, durum, bütçe.
  'ads',
  /// Mağaza senkronu yazdı: ürün, varyant, sipariş, stok.
  'stores',
  /// Uygulama içi bildirim düştü.
  'notifications',
  /// Bağlantı sağlığı değişti (koptu, bozuldu, düzeldi).
  'connections',
  /**
   * AI operatörü (Faz 5, 29 Eyl 2026): koşu kuyruğa girdi / başladı / bitti,
   * görev kuruldu / iptal / yeniden vadelendi, not yazıldı. `ref` biçimi
   * `run:<id>` · `task:<id>` · `note:<id>` (`operatorLiveRef`,
   * shared/operator.ts). Operatör ekranı bununla yoklamayı bırakıyor.
   * Yayıncılar: API (`LiveHub` — koşucu, görev/not servisleri, "şimdi bak",
   * olay tetiği) ve worker (`LivePublisher` — dağıtıcı, gözcü).
   */
  'operator',
  /**
   * Ajan önerisi (onay kartı) değişti (Faz 5): işlem planı arka planda adım
   * adım ilerliyor (`applying`) ve bitti. `ref` = `proposal:<id>`. Sohbet
   * kartı ve onay kuyruğu yeniden okuyor — veri taşımıyor.
   */
  'proposals',
  /**
   * Reklama yeni yorum geldi (Faz 7b, 30 Eyl 2026). `ref` = yerel reklam
   * kimliği (Google/YouTube'da reklam hesabı — yorum kanal genelinde
   * okunuyor). Olay METİN TAŞIMAZ; ekran yorumu kendi ucundan okur. Yayıncı
   * API `comments/comment-watch.service.ts` (tazelik zamanlayıcısının
   * ritmiyle, `commentsWatchedSeconds`).
   */
  'comments',
  /**
   * AI sohbetine SONRADAN satır yazıldı (1 Eki 2026) — takip görevinin sonucu
   * kaynak sohbete düştü. `ref` = `conversation:<id>`. Olay METİN TAŞIMAZ;
   * açık sohbet mesajlarını kendi ucundan (sahiplik denetimli) yeniden okur.
   * Yayıncı API `operator/operator-runner.service.ts` (`postFollowUp`).
   */
  'conversations',
  /**
   * ABONELİK DURUMU değişti (1 Eki 2026) — ödeme alındı, plan başladı/bitti,
   * yenileme reddedildi, ek süre başladı/bitti, iptal. Olay VERİ TAŞIMAZ;
   * kabuktaki abonelik şeridi ve günlük katman `GET /billing/alert`i yeniden
   * okuyor — "aboneliği başlattım ama şerit hâlâ duruyor" olmasın. Yayıncılar:
   * API (`BillingLiveBridge` — `billing.*` denetim kayıtları; `PayService`
   * yenileme reddi) ve worker (deneme bitişi, ek süre geçişleri).
   */
  'billing',
  /**
   * PRODÜKSİYON (reklam videosu) değişti (Kreatif stüdyo Faz 2, 1 Eki 2026) —
   * aşama ilerledi, kapı açıldı/kapandı, take QA'dan çıktı, teslim edildi.
   * `ref` = `production:<id>` (`productionLiveRef`, shared/production.ts).
   * Olay VERİ TAŞIMAZ; prodüksiyon sayfası, tezgâhın kuyruğu ve sohbetin
   * `production` kartı `GET /creatives/productions/:id`yi yeniden okuyor.
   * Yayıncılar: worker (`LivePublisher` — aşama işleri) ve API (`LiveHub` —
   * kullanıcı eylemleri: cevap, seçim, düzenleme, onay, iptal).
   */
  'productions',
  /**
   * MARKA KİTİ ya da MARKA HAFIZASI değişti (Dalga 5, 2 Eki 2026) — kit
   * kaydedildi, not eklendi/silindi/sabitlendi (sohbet ajanı, performans geri
   * beslemesi ya da başka bir sekme). `ref` = `kit` ya da `note:<id>`. Olay
   * VERİ TAŞIMAZ; Marka sayfası kendi uçlarından yeniden okur. Yayıncılar:
   * API (kit/not servisleri, sohbet aracı) ve worker (PRODUCTION/PERFORMANCE notu).
   */
  'brand',
  /**
   * KREATİF ÜRETİMİ değişti (Dalga 5b, 2 Eki 2026) — tek render işinin
   * (görsel, hızlı klip, UGC) AŞAMASI ilerledi, iş bitti/düştü/iptal edildi,
   * kreatif satırı değişti. `ref` = `job:<id>` (iş aşaması) ya da
   * `creative:<id>` (kreatif satırı) — `creativeLiveRef`,
   * shared/creative-progress.ts. Olay VERİ TAŞIMAZ; sohbetin canlı üretim
   * kartı ve geçmiş kutusu kendi ucundan (kiracı yalıtımlı) yeniden okur.
   * 🔴 Yalnız AŞAMA GEÇİŞİNDE yayınlanır, yüzde tikinde DEĞİL: yüzdeyi ekran
   * `creativeJobProgress`le zamandan kendisi yürütüyor — her yoklama turunda
   * olay itmek bütün açık panelleri boşuna okumaya zorlardı. Yayıncılar:
   * worker (`LivePublisher` — render aşamaları) ve API (`LiveHub` — üretim
   * başlatma, iptal, zorla kapatma).
   */
  'creatives',
] as const;
export type LiveTopic = (typeof LIVE_TOPICS)[number];

export function isLiveTopic(value: unknown): value is LiveTopic {
  return typeof value === 'string' && (LIVE_TOPICS as readonly string[]).includes(value);
}

/** SSE üzerinden ekrana giden olay. */
export interface LiveEvent {
  topic: LiveTopic;
  /// Değişen kaydın kimliği (reklam hesabı, mağaza bağlantısı). `null` = alan geneli.
  ref: string | null;
  /// Sunucu zamanı, ISO.
  at: string;
}

/// SSE olay adları — API yazar, panel dinler.
export const LIVE_SSE_EVENT = {
  /// Bağlantı kuruldu; `{ at }` taşır.
  READY: 'ready',
  /// Veri değişti; `LiveEvent` taşır.
  CHANGE: 'change',
} as const;

/**
 * Tazeleme ritmi — saniye.
 *
 * `Watched` = alan izlenirken (panel açık / API anahtarı okuyor / ajan
 * soruyor). `Background` = kimse bakmıyor; bildirim ve otomasyon bu ritimle
 * görür.
 */
export const LIVE_CADENCE = {
  adsWatchedSeconds: 60,
  adsBackgroundSeconds: 5 * 60,
  /**
   * Google Ads Basic erişim GÜNLÜK işlem kotası taşıyor (15.000). Tam senkron
   * kelime + arama terimi raporu da okuduğu için diğer platformların
   * ritminde koşsaydı birkaç hesap kotayı öğleden önce bitirirdi.
   */
  googleWatchedSeconds: 2 * 60,
  googleBackgroundSeconds: 15 * 60,
  /// İzlenen mağaza webhook'u olsa da olmasa da bu ritimle yoklanır:
  /// webhook saniyeler içinde getiriyor, yoklama kaçan webhook'un tabanı.
  storesWatchedSeconds: 60,
  storesBackgroundSeconds: 5 * 60,
  /// Kimse bakmazken webhook'u açık mağaza: tazeliği webhook taşıyor,
  /// yoklama yalnız ağ.
  storesWebhookBackgroundSeconds: 15 * 60,
  /**
   * REKLAM YORUMLARI (Faz 7b) — yeni yorum yoklaması. Reklam senkronuyla AYNI
   * "izleniyor mu" sorusu: panel açıkken 60 sn, kimse bakmazken 5 dk. Tek
   * yoklama hesap başına en fazla birkaç reklamı okuyor (sıra döner), yani
   * yüzlerce reklamlı hesapta bir reklamın yorumu en geç birkaç turda görünür.
   */
  commentsWatchedSeconds: 60,
  commentsBackgroundSeconds: 5 * 60,
  /**
   * YouTube Data API günlük kotası (10.000 birim) Google Ads kotasından AYRI ve
   * proje başına — bütün kiracılar paylaşıyor. Kanal geneli tek liste isteği
   * 1 birim + kanal okuması; 5/30 dk ritmi kiracı başına günde ~150–900 birim.
   */
  googleCommentsWatchedSeconds: 5 * 60,
  googleCommentsBackgroundSeconds: 30 * 60,
  /**
   * X API v2 yanıt okuması istek/gönderi başına ÜCRETLİ (geliştirici
   * uygulaması, kullandıkça öde) — izleme ayrıca sunucu bayrağına bağlı
   * (`X_COMMENTS_WATCH_ENABLED`, varsayılan kapalı) ve ritmi seyrek.
   */
  xCommentsWatchedSeconds: 10 * 60,
  xCommentsBackgroundSeconds: 60 * 60,
} as const;

/**
 * Arayüzün "canlı" diyebileceği en eski veri — saniye.
 *
 * İzlenen alanda veri en fazla `adsWatchedSeconds` + bir senkron süresi
 * eskir. Bundan eskiyse bir şey ters gidiyor (platform hata veriyor, kota
 * doldu) ve ekran bunu "X dk önce güncellendi" diye DÜRÜSTÇE söyler.
 */
export const LIVE_STALE_AFTER_SECONDS = 3 * 60;
